/* ============================================================
   Taglio tag renderer — vanilla JS port of taglio-tag.jsx
   ------------------------------------------------------------
   Stacked SVG layers in CSS 3D so thickness + raised parts read
   when the tag sways, tilts under the cursor, or spins.
   Contact info (phone) is printed on the BACK of the base.
   The nameplate carries only the pet's name.

   Mechanism (measured off the real Large print, 64 x 46.6 x 7 mm,
   cross-checked against photos of the finished tag):
     The base has a raised LIP around a recessed POCKET. Two ~10 mm
     SILVER DISC MAGNETS sit in wells in the pocket floor — they are
     visible, not sealed inside the plastic. The matching nameplate
     carries two magnets of its own on its back, so the plate drops
     into the pocket and is held by the magnets face to face.

     A shallow semicircular SCOOP is cut into the base material at the
     POCKET'S TOP EDGE — directly above the nameplate and centred under the
     tab. You hook a fingertip in it and lever the plate up. It is a
     depression, not a through-cut: it never reaches the pocket floor. The
     TAB itself is a SOLID rounded-rectangle lug — there is NO hole through
     it. The tag is drawn on its own: no split ring.

     Lift the plate (hover / exploded) and you see the magnets.

   API:
     const tag = TaglioTag.create({ baseColor, nameplateColor, ... });
     host.append(tag.el);
     tag.update({ icon: 'heart' });   // re-snaps / spins as needed
     tag.setShowBack(true);           // 180deg flip to the contact side

   Ported features: sway, pointer tilt, intro drop-in, spinOnChange,
   exploded, showBack, interactive (hover lifts the plate),
   seeded border-icon jitter, prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  const {
    FILAMENTS, BASE_COLORS, NAMEPLATE_COLORS, ICON_PATTERNS,
    autoIconColor, autoTextColor,
  } = window.TaglioCore;

  const SVG_NS = 'http://www.w3.org/2000/svg';

  // ---- keyframes + helper classes (injected once) ----
  function injectStyles() {
    if (document.getElementById('taglio-tag-styles')) return;
    const s = document.createElement('style');
    s.id = 'taglio-tag-styles';
    s.textContent = `
      @keyframes taglio-sway {
        0%   { transform: rotateZ(-2.6deg) rotateY(-16deg) rotateX(4deg); }
        100% { transform: rotateZ(2.6deg)  rotateY(16deg)  rotateX(4deg); }
      }
      @keyframes taglio-shadow-sway {
        0%   { transform: translateX(-4%) scaleX(0.94); }
        100% { transform: translateX(4%)  scaleX(0.94); }
      }
      @keyframes taglio-pop {
        0%   { transform: scale(0) rotate(-50deg); opacity: 0; }
        60%  { transform: scale(1.3) rotate(8deg); opacity: 1; }
        100% { transform: scale(1) rotate(0); opacity: 1; }
      }
      @keyframes taglio-bump {
        0% { transform: scale(0.9); } 55% { transform: scale(1.06); } 100% { transform: scale(1); }
      }
      .taglio-pop  { transform-box: fill-box; transform-origin: center; animation: taglio-pop 0.55s cubic-bezier(.3,1.4,.5,1) both; }
      .taglio-bump { transform-box: fill-box; transform-origin: center; animation: taglio-bump 0.35s ease-out both; }
      @media (prefers-reduced-motion: reduce) {
        .taglio-pop, .taglio-bump, .taglio-sway, .taglio-shadow { animation: none !important; }
      }
    `;
    document.head.appendChild(s);
  }

  const reducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- Icon border ----
  // Default: an even ring of 16 around the nameplate, with seeded jitter.
  // 6 across the top, 2 down the right, 6 across the bottom, 2 down the left.
  //
  // The BAT pattern is different, and deliberately so. The physical tag is
  // printed with a FIXED, hand-designed scatter of 14 bats — 6 along the top
  // (corners included), 6 along the bottom, 1 part-way down each side — laid
  // out with 180-degree rotational symmetry: turn the tag half a turn and
  // every bat lands on another with the same tilt. Measured from the product
  // photo, that symmetry holds to within 5 degrees of tilt and 0.013 of the
  // body width, which random jitter cannot reproduce. The printed bats are
  // also far larger than the generic ring: wingspan about 0.152 of the body
  // width, against roughly 0.06 for the generic size.
  //
  // The customer is buying the print they can see, so for the bat pattern we
  // reproduce the measured layout verbatim rather than randomising it.
  const BAT_BORDER = [
    // nx, ny: bat centre as a fraction of the body box. rot: tilt in degrees
    // from the artwork's head-up pose (SVG rotate, clockwise positive).
    [0.089, 0.155,  98],   // top-left corner        } top row
    [0.239, 0.155, 332],
    [0.410, 0.155, 212],
    [0.588, 0.155,  81],
    [0.739, 0.155, 306],
    [0.909, 0.155, 180],   // top-right corner
    [0.908, 0.845, 281],   // bottom-right corner    } bottom row (each is the
    [0.756, 0.845, 148],   //                          top-row bat turned 180)
    [0.588, 0.845,  28],
    [0.407, 0.845, 261],
    [0.248, 0.845, 121],
    [0.085, 0.845, 358],   // bottom-left corner
    [0.061, 0.503, 238],   // left side, mid height
    [0.933, 0.503,  62],   // right side, mid height
  ];
  // Wingspan of the printed bat, as a fraction of the body width. Measured
  // directly off the physical orange tag: the black print is ~0.126-0.132 of
  // the body width across all 14 bats (median 0.128). An earlier value of
  // 0.152 rendered the bats ~18% oversized — visible next to the real part.
  const BAT_SPAN = 0.128;
  // The bat artwork's own ink width inside its 20-unit box — turns the target
  // wingspan into the renderer's scale factor.
  const BAT_INK_W = 15.8;

  function borderIcons(bx, by, bw, bh, px, py, pw, ph, seedStr, iconKey) {
    if (iconKey === 'bat') {
      return {
        size: bw * BAT_SPAN * (20 / BAT_INK_W),
        spots: BAT_BORDER.map(function (b) {
          return { x: bx + b[0] * bw, y: by + b[1] * bh, rot: b[2], scale: 1 };
        }),
      };
    }
    let seed = 7;
    for (let i = 0; i < seedStr.length; i++) seed = (seed * 31 + seedStr.charCodeAt(i)) >>> 0;
    const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };

    const gx = px - bx, gy = py - by;
    const L = bx + gx * 0.5, R = bx + bw - gx * 0.5;
    const T = by + gy * 0.5, B = by + bh - gy * 0.5;
    const size = Math.min(gx, gy) * 0.6;
    const pts = [];
    const across = 6;
    for (let i = 0; i < across; i++) pts.push([L + (R - L) * i / (across - 1), T]);           // top
    for (let i = 1; i <= 2; i++) pts.push([R, T + (B - T) * i / 3]);                           // right
    for (let i = across - 1; i >= 0; i--) pts.push([L + (R - L) * i / (across - 1), B]);      // bottom
    for (let i = 2; i >= 1; i--) pts.push([L, T + (B - T) * i / 3]);                           // left

    const j = size * 0.12;
    return {
      size: size,
      spots: pts.map(function (p) {
        return {
          x: p[0] + (rand() - 0.5) * j,
          y: p[1] + (rand() - 0.5) * j,
          rot: (rand() - 0.5) * 56,
          scale: 0.9 + rand() * 0.18,
        };
      }),
    };
  }

  // ---- Geometry (from the real Large print, 64x40 face) ----
  function geometry(size) {
    const W = size;
    const bw = W * 0.9;
    const bh = bw * (40 / 64);
    const bx = (W - bw) / 2;
    // The tab is a rounded-rect LUG. On the Large it is 5.0 mm wide x 6.3 mm
    // tall — TALLER than it is wide, which is why the old half-round loop
    // always looked wrong. `half` is its half-width; `tabR` rounds the top.
    const r = bw * 0.065;
    const cx = W / 2;
    // The tab is a SOLID lug — there is NO hole through it. `half` is the
    // lug's half-width; `tabR` rounds its top corners. ~6 mm wide x 7 mm
    // tall on the Large.
    const th = bh * (7.0 / 40);
    const half = bw * (3.0 / 64);
    const f = bw * 0.03;
    const tabR = half * 0.5;
    // No split ring, so the lug's top is simply the top of the artwork —
    // there is no ring arc to tuck it behind any more.
    const tabTop = W * 0.035;
    const by = tabTop + th;
    const H = by + bh + W * 0.09;

    const pw = bw * (48.5 / 64);
    const ph = bh * (18.5 / 40);
    const px = bx + (bw - pw) / 2;
    const py = by + (bh - ph) / 2;
    const pr = ph * 0.14;

    // The pocket the nameplate drops into, and the lip that surrounds it.
    // The pocket is slightly larger than the plate so a ring of floor shows.
    const lipW = bw * 0.022;
    const pocketX = px - lipW;
    const pocketY = py - lipW;
    const pocketW = pw + lipW * 2;
    const pocketH = ph + lipW * 2;
    const pocketR = pr + lipW * 0.6;
    // Magnet wells: ~10.2 mm dia, 16 mm either side of centre (from the STL).
    const wellR = bw * (5.1 / 64);
    const wellDX = bw * (16 / 64);
    // Measured at y = -3.3 mm from the PART centre — which is the centre of the
    // 40 mm body once you exclude the 6.6 mm hanging tab. The pocket is centred
    // on the body too, so the wells sit on the pocket's centreline: offset 0.
    const wellDY = 0;
    // Fingertip relief. A shallow semicircular scoop cut into the base
    // material, springing from the pocket's TOP EDGE — directly above the
    // nameplate and centred under the tab. 3 mm radius: 6 mm across, 3 mm
    // deep. Fitted to the scoop profile measured in flatlay.jpg — the
    // measured mouth widths 3.34/4.39/5.06/5.44/5.63 mm sit on an r=2.9 mm
    // circle to within 0.2 mm, which is a semicircle and nothing else.
    const notchR = bw * (3.0 / 64);
    const notchCy = pocketY;   // the scoop springs from the pocket's top edge

    // The tab is a LUG, not a loop: straight sides, rounded top corners,
    // blended into the body with a small fillet. It is SOLID — one subpath,
    // no cutout.
    const silhouette = `
      M ${bx + r} ${by} L ${cx - half - f} ${by}
      Q ${cx - half} ${by} ${cx - half} ${by - f}
      L ${cx - half} ${tabTop + tabR}
      A ${tabR} ${tabR} 0 0 1 ${cx - half + tabR} ${tabTop}
      L ${cx + half - tabR} ${tabTop}
      A ${tabR} ${tabR} 0 0 1 ${cx + half} ${tabTop + tabR}
      L ${cx + half} ${by - f}
      Q ${cx + half} ${by} ${cx + half + f} ${by}
      L ${bx + bw - r} ${by} A ${r} ${r} 0 0 1 ${bx + bw} ${by + r}
      L ${bx + bw} ${by + bh - r} A ${r} ${r} 0 0 1 ${bx + bw - r} ${by + bh}
      L ${bx + r} ${by + bh} A ${r} ${r} 0 0 1 ${bx} ${by + bh - r}
      L ${bx} ${by + r} A ${r} ${r} 0 0 1 ${bx + r} ${by} Z`;

    return {
      W: W, H: H, bw: bw, bh: bh, bx: bx, by: by, th: th, r: r, cx: cx,
      half: half, f: f, tabTop: tabTop, tabR: tabR,
      pw: pw, ph: ph, px: px, py: py, pr: pr, silhouette: silhouette,
      lipW: lipW, pocketX: pocketX, pocketY: pocketY, pocketW: pocketW,
      pocketH: pocketH, pocketR: pocketR,
      wellR: wellR, wellDX: wellDX, wellDY: wellDY, notchR: notchR,
      notchCy: notchCy,
      small: W < 200,
    };
  }

  let uidCounter = 0;

  function create(initial) {
    injectStyles();

    const props = Object.assign({
      baseColor: 'black',
      nameplateColor: 'white',
      iconColor: 'auto',
      extraIconColors: [],
      iconMap: null,
      textColor: 'auto',
      icon: 'star',
      name: 'BELLA',
      size: 260,
      exploded: 0,
      interactive: false,
      tilt: true,
      sway: false,
      swayDelay: 0,
      intro: false,
      spinOnChange: false,
      phone: '',
      // Cart lines set this so a missing number prints a visible
      // "PHONE MISSING" warning instead of silently dropping the line.
      phoneWarning: false,
      showBack: false,
      plate: true,
      onClick: null,
    }, initial || {});

    const state = {
      reduced: reducedMotion(),
      hover: false,
      showBack: !!props.showBack,
      popKey: 0,
      firstRender: true,
    };

    // Geometry + depth are rebuilt only when the size changes.
    let geo = null;
    let depth = null;
    let layerEls = null;
    let wrapperEls = null;
    let existingHost = null;

    const uid = 'tg' + (++uidCounter) + Math.random().toString(36).slice(2, 6);
    const texId = 'tx' + uid;
    const magId = 'mg' + uid;
    const notchId = 'nt' + uid;

    // ---- element factories ----
    function el(tag, style, attrs) {
      const n = document.createElement(tag);
      if (style) Object.assign(n.style, style);
      if (attrs) for (const k in attrs) n.setAttribute(k, attrs[k]);
      return n;
    }

    function mkLayer(z, dy, extra) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('width', geo.W);
      svg.setAttribute('height', geo.H);
      svg.setAttribute('viewBox', '0 0 ' + geo.W + ' ' + geo.H);
      Object.assign(svg.style, {
        position: 'absolute', left: '0', top: '0', overflow: 'visible',
        pointerEvents: 'none',
        transform: 'translate3d(0, ' + dy + 'px, ' + z + 'px)' + (extra || ''),
      });
      return svg;
    }

    function setBackface(node, hide) {
      node.style.backfaceVisibility = hide ? 'hidden' : 'visible';
      node.style.webkitBackfaceVisibility = hide ? 'hidden' : 'visible';
    }

    // ---- resolved colors ----
    function resolved(p) {
      const bKey = BASE_COLORS[p.baseColor] ? p.baseColor : 'black';
      const pKey = NAMEPLATE_COLORS[p.nameplateColor] ? p.nameplateColor : 'white';
      // eslint-disable-next-line no-nested-ternary
      const icKey = (p.iconColor === 'auto' || !FILAMENTS[p.iconColor]) ? autoIconColor(bKey) : p.iconColor;
      const tcKey = (p.textColor === 'auto' || !FILAMENTS[p.textColor]) ? autoTextColor(pKey, bKey) : p.textColor;
      const palette = [FILAMENTS[icKey]].concat((p.extraIconColors || []).map(k => FILAMENTS[k]).filter(Boolean));
      return { bKey: bKey, pKey: pKey, icKey: icKey, tcKey: tcKey, palette: palette };
    }

    function iconFillFor(palette, map, i) {
      if (palette.length === 1) return palette[0];
      const idx = (map && map[i] != null) ? map[i] : i % palette.length;
      return palette[idx] || palette[0];
    }

    // ---- content builders ----
    function plateRectHTML(fill, extra) {
      return '<rect x="' + geo.px + '" y="' + geo.py + '" width="' + geo.pw +
             '" height="' + geo.ph + '" rx="' + geo.pr + '" fill="' + fill + '"' +
             (extra || '') + ' />';
    }

    // NOTE: these strings are parsed by the HTML parser, so SVG attributes must
    // use their hyphenated names (font-size, text-anchor, fill-rule, ...).
    // camelCase names get lowercased and silently dropped.
    function textHTML(fill, anim) {
      const label = state.label;
      return '<g' + (anim && !state.reduced ? ' class="taglio-bump"' : '') + '>' +
        '<text x="' + (geo.px + geo.pw / 2) + '" y="' + (geo.py + geo.ph / 2) +
        '" dy="0.36em" text-anchor="middle" font-family=\'"Nunito", "Quicksand", system-ui, sans-serif\' ' +
        'font-weight="900" font-size="' + depth.fontSize + '" letter-spacing="0.03em" fill="' + fill + '">' +
        label + '</text></g>';
    }

    function faceHTML(bc) {
      const W = geo.W;
      return '<defs><pattern id="' + texId + '" patternUnits="userSpaceOnUse" width="' +
        Math.max(2, W * 0.011) + '" height="' + Math.max(2, W * 0.011) + '" patternTransform="rotate(40)">' +
        '<rect width="100%" height="50%" fill="rgba(255,255,255,0.05)" />' +
        '<rect y="50%" width="100%" height="50%" fill="rgba(0,0,0,0.04)" /></pattern>' +
        '<radialGradient id="' + magId + '" cx="38%" cy="30%" r="78%">' +
        '<stop offset="0%" stop-color="#fbfcfd" />' +
        '<stop offset="45%" stop-color="#c8ced6" />' +
        '<stop offset="100%" stop-color="#8a919b" />' +
        '</radialGradient></defs>' +
        '<path d="' + geo.silhouette + '" fill="' + bc.base + '" fill-rule="evenodd" />' +
        '<path d="' + geo.silhouette + '" fill="url(#' + texId + ')" fill-rule="evenodd" />' +
        '<path d="' + geo.silhouette + '" fill="none" fill-rule="evenodd" ' +
        'stroke="rgba(255,255,255,0.16)" stroke-width="' + Math.max(0.6, W * 0.003) + '" />' +
        pocketHTML(bc) + wellsHTML(bc) +
        '<rect x="' + geo.pocketX + '" y="' + geo.pocketY + '" width="' + geo.pocketW +
        '" height="' + geo.pocketH + '" rx="' + geo.pocketR + '" fill="none" ' +
        'stroke="rgba(255,255,255,0.20)" stroke-width="' + Math.max(0.6, W * 0.003) + '" />';
    }

    // The recessed pocket the nameplate drops into. Drawn a shade darker than
    // the face, with a shadow along the top inner wall, so it reads as a cavity
    // rather than a printed rectangle.
    function pocketHTML(bc) {
      return '<rect x="' + geo.pocketX + '" y="' + geo.pocketY + '" width="' + geo.pocketW +
        '" height="' + geo.pocketH + '" rx="' + geo.pocketR + '" fill="' + bc.side +
        '" opacity="0.92" />' +
        '<rect x="' + geo.pocketX + '" y="' + geo.pocketY + '" width="' + geo.pocketW +
        '" height="' + (geo.pocketH * 0.26) + '" rx="' + geo.pocketR +
        '" fill="rgba(0,0,0,0.15)" />';
    }

    // The two magnet wells, each holding a VISIBLE silver disc magnet. These
    // are not sealed inside the plastic — pull the nameplate out and you look
    // straight at them. Measured off the print: 10.1 mm across, 16 mm either
    // side of centre, sunk 1.2 mm into the pocket floor.
    function wellsHTML(bc) {
      const W = geo.W;
      const cy = geo.py + geo.ph / 2 + geo.wellDY;
      const r = geo.wellR;
      const sw = Math.max(0.5, W * 0.0025);
      const mr = r * 0.82;                        // the magnet disc
      return [geo.cx - geo.wellDX, geo.cx + geo.wellDX].map(function (cx) {
        return '<circle cx="' + cx + '" cy="' + cy + '" r="' + r +
          '" fill="' + bc.side + '" />' +
          '<circle cx="' + cx + '" cy="' + cy + '" r="' + r +
          '" fill="rgba(0,0,0,0.42)" />' +
          // the magnet, sitting a shade low in its well
          '<circle cx="' + cx + '" cy="' + (cy + r * 0.06) + '" r="' + mr +
          '" fill="url(#' + magId + ')" />' +
          // specular streak — the giveaway that it is metal, not plastic
          '<path d="M ' + (cx - mr * 0.62) + ' ' + (cy - mr * 0.18) +
          ' a ' + mr + ' ' + mr + ' 0 0 1 ' + (mr * 0.94) + ' ' + (-mr * 0.30) +
          '" fill="none" stroke="rgba(255,255,255,0.75)" stroke-width="' + (r * 0.14) +
          '" stroke-linecap="round" />' +
          '<circle cx="' + cx + '" cy="' + (cy + r * 0.06) + '" r="' + mr +
          '" fill="none" stroke="rgba(0,0,0,0.38)" stroke-width="' + sw + '" />';
      }).join('');
    }

    // Fingertip relief. A semicircular scoop cut into the base material at the
    // pocket's TOP EDGE — directly above the nameplate and centred under the
    // tab. Its arch breaks THROUGH the raised lip band, so the band is
    // interrupted right where a fingertip needs to go to pry the plate out.
    // It is still shallow in the body's thickness — it never reaches the
    // pocket floor — so it is painted, not punched into the silhouette. A flat
    // fill read as a black hole and a lit rim read as a raised bump; what makes
    // it read as cut away is the dark cavity, the crisp cut edge, and the
    // stubs of lip band left standing either side of the opening.
    function notchHTML(bc) {
      const cx = geo.cx, cy = geo.notchCy, r = geo.notchR;
      const sw = Math.max(0.6, geo.W * 0.004);
      // sweep=1 in SVG's y-down space arcs UP, so the scoop bulges above the
      // pocket's top edge — a semicircle standing on that edge.
      const arc = 'M ' + (cx - r) + ' ' + cy +
        ' A ' + r + ' ' + r + ' 0 0 1 ' + (cx + r) + ' ' + cy;
      // The scoop breaks THROUGH the lip band: the arch rises higher than the
      // band is thick, so it cuts the rim and opens into the face above it.
      // That opening is the whole point — it is where a fingertip gets under
      // the plate. So the arch is painted as material REMOVED: a dark cavity,
      // a crisp cut edge, and the cut ends of the band where it passes through.
      const t = Math.max(0.8, geo.W * 0.016);        // the lip band's thickness
      const hx = Math.sqrt(Math.max(0, r * r - t * t)); // where the arch cuts the band
      return '<defs><linearGradient id="' + notchId + '" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0%" stop-color="#000000" stop-opacity="0.48" />' +
          '<stop offset="58%" stop-color="#000000" stop-opacity="0.22" />' +
          '<stop offset="100%" stop-color="#000000" stop-opacity="0.06" />' +
        '</linearGradient></defs>' +
        // the cavity wall, a shade below the face it is cut into
        '<path d="' + arc + ' Z" fill="' + bc.side + '" />' +
        // deepest at the top, where the overhanging material cuts off the light
        '<path d="' + arc + ' Z" fill="url(#' + notchId + ')" />' +
        // the cut ends of the lip band, left standing where the arch passes
        // through — without these the band reads as continuous and the scoop
        // looks stuck on instead of cut out
        '<path d="M ' + (cx - hx) + ' ' + (cy - t) + ' L ' + (cx - hx) + ' ' + cy +
        ' M ' + (cx + hx) + ' ' + (cy - t) + ' L ' + (cx + hx) + ' ' + cy +
        '" fill="none" stroke="' + bc.base + '" stroke-width="' + (geo.W * 0.006) + '" />' +
        // the cut edge itself: a thin dark line, so the top reads as cut away
        '<path d="' + arc + '" fill="none" stroke="rgba(0,0,0,0.42)" stroke-width="' +
        sw + '" stroke-linecap="round" />';
    }

    // The raised lip, as its own layer above the face. A stroked band around the
    // pocket boundary reads as the rim the plate sits behind.
    function lipHTML(bc) {
      const W = geo.W;
      const t = Math.max(0.8, W * 0.016);
      return '<rect x="' + geo.pocketX + '" y="' + geo.pocketY + '" width="' + geo.pocketW +
        '" height="' + geo.pocketH + '" rx="' + geo.pocketR + '" fill="none" ' +
        'stroke="' + bc.base + '" stroke-width="' + (t * 2) + '" />';
    }

    // The back of the base is the whole reason the phone exists: a stranger who
    // finds the dog reads it. So this must NEVER invent a number. It used to
    // fall back to a sample '(555) 014-2290', which meant a real cart line with
    // no phone printed a fake number the owner would never be called on.
    //
    // The header and the number are ONE block. Printing "IF FOUND, PLEASE CALL"
    // with nothing under it is worse than printing nothing: it reads as a tag
    // that wants to be called and gives no way to do it. So a tag with no phone
    // drops the whole block (decorative tags, which never turn around) unless
    // phoneWarning is set, in which case the block appears with a red
    // "PHONE MISSING" — a fulfilment problem the cart should shout about.
    function backHTML(bc, ic, phone, phoneWarning) {
      const cx = geo.cx, by = geo.by, bh = geo.bh, bw = geo.bw;
      const ph = String(phone || '').trim();
      const missing = !ph;

      let callBlock = '';
      if (!missing || phoneWarning) {
        const line = missing ? 'PHONE MISSING' : ph;
        const callSize = Math.min(bh * 0.19, (bw * 0.84) / (Math.max(line.length, 8) * 0.6));
        callBlock =
          '<text x="' + cx + '" y="' + (by + bh * 0.3) + '" font-size="' + (bh * 0.085) +
          '" letter-spacing="0.12em">IF FOUND, PLEASE CALL</text>' +
          '<text x="' + cx + '" y="' + (by + bh * 0.56) + '" font-size="' + callSize + '"' +
          (missing ? ' fill="#B3261E"' : '') + '>' + line + '</text>';
      }

      return '<path d="' + geo.silhouette + '" fill="' + bc.base + '" fill-rule="evenodd" />' +
        '<path d="' + geo.silhouette + '" fill="url(#' + texId + ')" fill-rule="evenodd" />' +
        '<g font-family=\'"Nunito", system-ui, sans-serif\' font-weight="900" text-anchor="middle" fill="' + ic.base + '">' +
        callBlock +
        '<text x="' + cx + '" y="' + (by + bh * 0.8) + '" font-size="' + (bh * 0.07) +
        '" letter-spacing="0.2em" opacity="0.75">TAGLIO</text></g>';
    }

    // ---- structure ----
    function buildDepth() {
      const cols = resolved(props);
      const ic = FILAMENTS[cols.icKey];
      const spots = borderIcons(geo.bx, geo.by, geo.bw, geo.bh, geo.px, geo.py, geo.pw, geo.ph,
        cols.bKey + props.icon, props.icon);
      const label = (props.name || 'BELLA').toUpperCase();
      const fontSize = Math.min(geo.ph * 0.52,
        (geo.pw * 0.86) / (Math.max(label.length, 3) * 0.72));

      depth = {
        spots: spots.spots,
        iconSize: spots.size,
        iconPath: (ICON_PATTERNS[props.icon] || ICON_PATTERNS.star).path,
        fontSize: fontSize,
        D: geo.bw * (6 / 64),                       // 6 mm body
        nBase: geo.small ? 3 : 8,
        restDrop: geo.W * 0.012,                    // 2D thickness cue at rest
        iconZ: geo.bw * (1 / 64),
        // The lip stands ~3 mm proud of the pocket floor (STL: floor 7.5,
        // lip 10.5). The plate drops INSIDE the lip but its face sits a hair
        // proud of the rim — look at the finished tag side-on and you can see
        // the slab's edge and a shadow under it. That edge is what tells you
        // the plate is a separate piece.
        lipZ: geo.bw * (3 / 64),
        plateZ: geo.bw * (3.35 / 64),
        nPlate: geo.small ? 1 : 3,
        textZ: geo.W * 0.009,
      };
      state.label = label;
      return cols;
    }

    function buildDOM() {
      const cols = buildDepth();
      const bc = BASE_COLORS[cols.bKey];
      const np = NAMEPLATE_COLORS[cols.pKey];
      const ic = FILAMENTS[cols.icKey];
      const tc = FILAMENTS[cols.tcKey];

      // Reuse the host element across rebuilds so listeners survive a resize.
      const host = existingHost || el('div', {});
      Object.assign(host.style, {
        position: 'relative', display: 'inline-block',
        width: geo.W + 'px', height: geo.H + 'px',
        perspective: (geo.W * 3.2) + 'px',
        flexShrink: '0',
      });
      host.innerHTML = '';

      // Ground shadow
      const shadow = el('div', {
        position: 'absolute', left: (geo.bx + geo.bw * 0.08) + 'px',
        width: (geo.bw * 0.84) + 'px',
        top: (geo.by + geo.bh + geo.W * 0.02) + 'px',
        height: (geo.W * 0.05) + 'px',
        background: 'radial-gradient(ellipse at center, rgba(20,20,30,0.3), rgba(20,20,30,0) 70%)',
        filter: 'blur(' + Math.max(2, geo.W * 0.012) + 'px)',
        pointerEvents: 'none',
      });

      // Sway (pendulum from the hole)
      const swayEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        // Sway pivots from where the tab meets the body, so the tag hangs
        // off its lug rather than spinning about an imaginary hole.
        transformOrigin: geo.cx + 'px ' + (geo.by - geo.th * 0.3) + 'px',
      });
      // Pointer tilt
      const tiltEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        transformOrigin: geo.cx + 'px ' + (geo.by + geo.bh / 2) + 'px',
      });
      // Flip to back (contact side)
      const flipEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        transformOrigin: geo.cx + 'px ' + (geo.by + geo.bh / 2) + 'px',
        transform: 'rotateY(0deg)',
        transition: state.reduced ? 'none' : 'transform 0.9s cubic-bezier(.5,0,.2,1.15)',
      });
      // Spin on change
      const spinEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        transformOrigin: geo.cx + 'px ' + (geo.by + geo.bh / 2) + 'px',
      });

      // Back face — contact info lives here
      const backEl = mkLayer(-depth.D - 0.6, depth.restDrop, ' rotateY(180deg)');
      setBackface(backEl, true);

      // Base walls
      const wallEls = [];
      for (let i = 0; i < depth.nBase; i++) {
        const t = (depth.nBase - i) / depth.nBase;
        wallEls.push(mkLayer(-depth.D * t, depth.restDrop * t));
      }

      // Base top face + recess well
      const faceEl = mkLayer(0, 0);
      setBackface(faceEl, true);

      // Raised icons: wall + top
      const iconWallEl = mkLayer(depth.iconZ * 0.45, depth.restDrop * 0.3);
      const iconTopEl = mkLayer(depth.iconZ, 0);
      setBackface(iconWallEl, true);
      setBackface(iconTopEl, true);

      // Plate wrapper (lifts out of the well when exploded)
      const plateWrapEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        transform: 'translate3d(0, 0, 0)',
        transition: 'transform 0.6s cubic-bezier(.3,1.45,.5,1)',
      });
      const plateAnimEl = el('div', {
        position: 'absolute', inset: '0', transformStyle: 'preserve-3d',
        transformOrigin: geo.cx + 'px ' + (geo.py + geo.ph / 2) + 'px',
      });

      // Raised lip: a ring of material standing proud of the pocket floor.
      const lipEl = mkLayer(depth.lipZ, 0);
      setBackface(lipEl, true);

      const plateWallEls = [];
      for (let i = 0; i < depth.nPlate; i++) {
        const t = (i + 1) / (depth.nPlate + 1);
        const w = mkLayer(depth.plateZ * t, depth.restDrop * 0.5 * (1 - t) + depth.restDrop * 0.12);
        setBackface(w, true);
        plateWallEls.push(w);
      }
      const plateTopEl = mkLayer(depth.plateZ, 0);
      setBackface(plateTopEl, true);
      const textWallEl = mkLayer(depth.plateZ + depth.textZ * 0.45, depth.restDrop * 0.28);
      const textTopEl = mkLayer(depth.plateZ + depth.textZ, 0);
      setBackface(textWallEl, true);
      setBackface(textTopEl, true);

      plateWallEls.forEach(function (w) { plateAnimEl.appendChild(w); });
      plateAnimEl.appendChild(plateTopEl);
      plateAnimEl.appendChild(textWallEl);
      plateAnimEl.appendChild(textTopEl);
      plateWrapEl.appendChild(plateAnimEl);

      spinEl.appendChild(backEl);
      wallEls.forEach(function (w) { spinEl.appendChild(w); });
      spinEl.appendChild(faceEl);
      spinEl.appendChild(lipEl);
      spinEl.appendChild(iconWallEl);
      spinEl.appendChild(iconTopEl);
      spinEl.appendChild(plateWrapEl);

      flipEl.appendChild(spinEl);
      tiltEl.appendChild(flipEl);
      swayEl.appendChild(tiltEl);
      host.appendChild(shadow);
      host.appendChild(swayEl);

      layerEls = {
        back: backEl, walls: wallEls, face: faceEl, lip: lipEl,
        iconWall: iconWallEl, iconTop: iconTopEl,
        plateWall: plateWallEls, plateTop: plateTopEl,
        textWall: textWallEl, textTop: textTopEl,
      };
      wrapperEls = {
        host: host, shadow: shadow, sway: swayEl, tilt: tiltEl,
        flip: flipEl, spin: spinEl, plateWrap: plateWrapEl, plateAnim: plateAnimEl,
      };

      return { bc: bc, np: np, ic: ic, tc: tc };
    }

    // ---- paint (fills + content) ----
    let lastResolved = null;

    function paint() {
      const cols = resolved(props);
      const bc = BASE_COLORS[cols.bKey];
      const np = NAMEPLATE_COLORS[cols.pKey];
      const ic = FILAMENTS[cols.icKey];
      const tc = FILAMENTS[cols.tcKey];
      lastResolved = cols;

      layerEls.back.innerHTML = backHTML(bc, ic, props.phone, props.phoneWarning);
      layerEls.walls.forEach(function (w) { w.innerHTML = '<path d="' + geo.silhouette + '" fill="' + bc.side + '" fill-rule="evenodd" />'; });
      layerEls.face.innerHTML = faceHTML(bc);
      // The fingertip scoop is cut at the pocket's top edge, so it is painted
      // on the LIP layer — after the lip stroke — or that bright band would
      // slice straight across the mouth of the scoop.
      layerEls.lip.innerHTML = lipHTML(bc) + notchHTML(bc);
      // icon wall uses the "side" shade; icon top uses the face shade
      layerEls.iconWall.innerHTML = iconsGroupHTMLShaded(cols.palette, props.iconMap, 'side', false);
      layerEls.iconTop.innerHTML = iconsGroupHTMLShaded(cols.palette, props.iconMap, 'base', true);

      layerEls.plateWall.forEach(function (w) { w.innerHTML = plateRectHTML(np.side); });
      layerEls.plateTop.innerHTML = plateRectHTML(np.base) +
        plateRectHTML('url(#' + texId + ')') +
        // the slab casts a shadow onto the pocket floor it sits in
        '<rect x="' + geo.px + '" y="' + (geo.py + geo.ph * 0.90) + '" width="' + geo.pw +
        '" height="' + (geo.ph * 0.18) + '" rx="' + geo.pr +
        '" fill="rgba(0,0,0,0.28)" />' +
        plateRectHTML('none', ' stroke="rgba(255,255,255,0.22)" stroke-width="' +
          Math.max(0.6, geo.W * 0.003) + '"');
      layerEls.textWall.innerHTML = textHTML(tc.side, false);
      layerEls.textTop.innerHTML = textHTML(tc.base, true);
    }

    // variant of the icon group with a chosen shade face
    function iconsGroupHTMLShaded(palette, map, face, withAnim) {
      let out = '<g>';
      const spots = depth.spots;
      for (let i = 0; i < spots.length; i++) {
        const p = spots[i];
        const ic = iconFillFor(palette, map, i);
        const anim = withAnim && !state.reduced
          ? ' class="taglio-pop" style="animation-delay:' + (120 + i * 45) + 'ms"'
          : '';
        out += '<g transform="translate(' + p.x + ' ' + p.y + ') rotate(' + p.rot +
               ') scale(' + (depth.iconSize * p.scale / 20) + ') translate(-10 -10)">' +
               '<g' + anim + '>' +
               '<path d="' + depth.iconPath + '" fill="' + ic[face] + '" fill-rule="evenodd" />' +
               '</g></g>';
      }
      return out + '</g>';
    }

    // ---- motion helpers ----
    function snapPlate(delay) {
      const el2 = wrapperEls.plateAnim;
      if (!el2 || state.reduced || !el2.animate) return;
      el2.animate([
        { transform: 'translate3d(0, ' + (-geo.bh * 0.85) + 'px, ' + (geo.W * 0.3) + 'px) rotateX(-30deg)', opacity: 0 },
        { transform: 'translate3d(0, ' + (geo.bh * 0.025) + 'px, 0px) rotateX(0deg)', opacity: 1, offset: 0.62 },
        { transform: 'translate3d(0, ' + (-geo.bh * 0.03) + 'px, ' + (geo.W * 0.02) + 'px)', offset: 0.8 },
        { transform: 'translate3d(0,0,0)' },
      ], { duration: 950, delay: delay || 0, easing: 'cubic-bezier(.35,0,.25,1)', fill: 'backwards' });
    }

    function spinOnce() {
      if (state.reduced || !wrapperEls.spin.animate) return;
      wrapperEls.spin.animate(
        [{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(360deg)' }],
        { duration: 1000, easing: 'cubic-bezier(.55,0,.2,1)' }
      );
    }

    function applySway() {
      const on = (props.sway) && !state.reduced;
      wrapperEls.sway.style.animation = on
        ? 'taglio-sway 3.4s ease-in-out ' + (props.swayDelay || 0) + 's infinite alternate' : 'none';
      if (on) wrapperEls.sway.classList.add('taglio-sway');
      else wrapperEls.sway.classList.remove('taglio-sway');
      wrapperEls.shadow.style.animation = on
        ? 'taglio-shadow-sway 3.4s ease-in-out ' + (props.swayDelay || 0) + 's infinite alternate' : 'none';
      if (on) wrapperEls.shadow.classList.add('taglio-shadow');
      else wrapperEls.shadow.classList.remove('taglio-shadow');
    }

    function applyExplode() {
      const ex = props.interactive ? Math.max(props.exploded, state.hover ? 1 : 0) : props.exploded;
      const liftY = -ex * geo.bh * 0.32;
      const liftZ = ex * geo.W * 0.2;
      // Tip the plate's top edge away as it rises. A pure translate shows the
      // plate's FRONT and nothing else, so it reads as a decal floating off the
      // base rather than a separate slab. Rotating on X brings the near edge up
      // and lets you see under it — which is where the plate's own two magnets
      // live, the half of the pairing the photos show when you turn it over.
      const tiltX = -ex * 18;
      wrapperEls.plateWrap.style.transform =
        'translate3d(0, ' + liftY + 'px, ' + liftZ + 'px) rotateX(' + tiltX + 'deg)';
      wrapperEls.plateWrap.style.display = props.plate ? '' : 'none';
    }

    function applyBack() {
      wrapperEls.flip.style.transform = state.showBack ? 'rotateY(180deg)' : 'rotateY(0deg)';
    }

    function applyCursor() {
      wrapperEls.host.style.cursor = (props.onClick || props.interactive) ? 'pointer' : 'default';
    }

    // ---- build ----
    geo = geometry(props.size);
    buildDOM();
    existingHost = wrapperEls.host;
    paint();
    applySway();
    applyExplode();
    applyBack();
    applyCursor();

    if (props.intro) setTimeout(function () { state.firstRender = false; }, 0);
    if (props.intro) snapPlate(450);
    setTimeout(function () { state.firstRender = false; }, 0);

    // ---- interaction ----
    function onMove(e) {
      if (!props.tilt || state.reduced) return;
      const rect = wrapperEls.host.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      wrapperEls.tilt.style.transition = 'transform 0.18s ease-out';
      wrapperEls.tilt.style.transform = 'rotateX(' + (-y * 34) + 'deg) rotateY(' + (x * 40) + 'deg)';
    }
    function onLeave() {
      if (props.interactive) { state.hover = false; applyExplode(); }
      wrapperEls.tilt.style.transition = 'transform 0.9s cubic-bezier(.3,1.6,.4,1)';
      wrapperEls.tilt.style.transform = 'rotateX(0deg) rotateY(0deg)';
    }
    function onEnter() {
      if (props.interactive) { state.hover = true; applyExplode(); }
    }

    wrapperEls.host.addEventListener('pointermove', onMove);
    wrapperEls.host.addEventListener('pointerleave', onLeave);
    wrapperEls.host.addEventListener('pointerenter', onEnter);
    wrapperEls.host.addEventListener('click', function (e) {
      if (props.onClick) props.onClick(e);
    });

    // ---- update ----
    function update(next) {
      const prev = Object.assign({}, props);
      Object.assign(props, next || {});

      // Size change needs a rebuild (geometry + layer count)
      if (next && next.size != null && next.size !== prev.size) {
        const hostParent = wrapperEls.host;
        hostParent.innerHTML = '';
        geo = geometry(props.size);
        const b2 = buildDOM();
        paint();
        applySway(); applyExplode(); applyBack(); applyCursor();
        if (props.intro) snapPlate(0);
        return api;
      }

      // Icon/color/extras change the seam + the seeded jitter (and pop icons).
      // A name change only needs the depth rebuilt (label + shrink-to-fit size).
      const structuralChanged = props.icon !== prev.icon ||
        props.baseColor !== prev.baseColor ||
        JSON.stringify(props.extraIconColors) !== JSON.stringify(prev.extraIconColors) ||
        props.size !== prev.size;
      const labelChanged = props.name !== prev.name;

      if (structuralChanged || labelChanged) buildDepth();
      if (structuralChanged) state.popKey++;

      // Icons + face + walls always repaint cheaply
      const cols = resolved(props);
      paint();

      // Spin + plate re-snap on the right changes
      if (!state.firstRender) {
        if (structuralChanged && props.spinOnChange) spinOnce();
        if ((props.nameplateColor !== prev.nameplateColor || props.textColor !== prev.textColor) && props.spinOnChange) {
          snapPlate(0);
        }
      }

      applySway();
      applyExplode();
      applyBack();
      applyCursor();
      return api;
    }

    function setShowBack(v) {
      state.showBack = !!v;
      applyBack();
    }

    function destroy() {
      wrapperEls.host.removeEventListener('pointermove', onMove);
      wrapperEls.host.removeEventListener('pointerleave', onLeave);
      wrapperEls.host.removeEventListener('pointerenter', onEnter);
      if (wrapperEls.host.parentNode) wrapperEls.host.parentNode.removeChild(wrapperEls.host);
    }

    const api = { el: wrapperEls.host, update: update, setShowBack: setShowBack, destroy: destroy, props: props };
    return api;
  }

  window.TaglioTag = { create: create, injectStyles: injectStyles };
})();
